"use client";

import React, { useCallback, useRef } from "react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CustomValuePicker } from "@/components/CustomValuePicker";
import { insertAtCursor } from "@/lib/insertAtCursor";
import { cn } from "@/lib/utils";

/**
 * Merge react-hook-form's `ref` (used for focus-on-error) with our own ref to
 * the underlying DOM element (used to read the caret position).
 */
function useMergedRef<T>(forwarded: React.Ref<T> | undefined) {
  const inner = useRef<T | null>(null);
  const setRefs = useCallback(
    (node: T | null) => {
      inner.current = node;
      if (typeof forwarded === "function") {
        forwarded(node);
      } else if (forwarded) {
        (forwarded as React.MutableRefObject<T | null>).current = node;
      }
    },
    [forwarded]
  );
  return [inner, setRefs] as const;
}

type InputWithPickerProps = React.ComponentProps<typeof Input>;

/** An `Input` with a custom value picker pinned to its right edge. */
export function InputWithPicker({
  className,
  ref,
  ...props
}: InputWithPickerProps) {
  const [inner, setRefs] = useMergedRef<HTMLInputElement>(ref);

  return (
    <div className="relative">
      <Input ref={setRefs} className={cn("pr-9", className)} {...props} />
      <div className="absolute right-1 top-1/2 -translate-y-1/2">
        <CustomValuePicker
          onSelect={(value) => insertAtCursor(inner.current, value)}
        />
      </div>
    </div>
  );
}

type TextareaWithPickerProps = React.ComponentProps<typeof Textarea>;

/** A `Textarea` with a custom value picker pinned to its top-right corner. */
export function TextareaWithPicker({
  className,
  ref,
  ...props
}: TextareaWithPickerProps) {
  const [inner, setRefs] = useMergedRef<HTMLTextAreaElement>(ref);

  return (
    <div className="relative">
      <Textarea ref={setRefs} className={cn("pr-9", className)} {...props} />
      <div className="absolute right-1 top-1">
        <CustomValuePicker
          onSelect={(value) => insertAtCursor(inner.current, value)}
        />
      </div>
    </div>
  );
}
